import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { setErrorMessage, setIsLoading, setIsToasterError } from "./app";
import API from "../../api/api";
import { setAdminLoader } from "./admin";

export interface ITonneVerschwundenCreateBody {
  place: string;
  street: string;
  homeNumber: string;
  hausNrZusatz: string;
  phoneNumber: string;
  email: string;
  userName: string;
  userSurname: string;
  tonnenType: string;
  tonnenVolume: string;
  tonnenNummern: string[];
  verschwundeneTonne?: string;
  homeNumberInfo?: string | null;
}

export interface ITonneVerschwundenResponseBody extends ITonneVerschwundenCreateBody {
  id: string;
  createdAt: string;
  status?: string;
  history?: {
    id: string;
    createdAt: string;
    status: string;
    email: string;
    firstName?: string;
    lastName?: string;
  }[];
  objectId?: string;
  kassenzeichen?: string;
  behid?: string | number;
  calenderWeek?: string;
  updatedStatusType?: string;
  adminMessage?: string;
  kavnr?: number;
}

export interface IReclamation {
  client: {
    places: {
      id: string;
      gemeindeBezeichnung: string;
      gemeindePostleitzahl: number;
      gemeindeNummer: number;
    }[];
    cityParts: {
      id: string;
      orteilBezeichnung: string;
      ortsteilNummer: number;
      ortsteilBezirk: string;
      gemeindeNummer: string;
    }[];
    streets: {
      id: string;
      gemeindeNummer: number;
      strasseBezeichnung: string;
      ortsteilNummer?: string;
    }[];
    errorMessage: string;
  };
  admin: {
    itemsCount: number;
    tonneVerschwundenData: ITonneVerschwundenResponseBody[];
    tonneVerschwundenDataItem: ITonneVerschwundenResponseBody | null;
    isTonneVerschwundenDataLoading: boolean;
  };
}

const initialState: IReclamation = {
  client: {
    places: [],
    streets: [],
    cityParts: [],
    errorMessage: "",
  },
  admin: {
    itemsCount: 0,
    tonneVerschwundenData: [],
    isTonneVerschwundenDataLoading: false,
    tonneVerschwundenDataItem: null,
  },
};

export const getTonneDeffektStreetsAndPlaces = createAsyncThunk(
  "tonneVerschwunden/client/getTonneDeffektStreetsAndPlaces",
  async (_, { dispatch }) => {
    try {
      dispatch(setIsLoading(true));
      const data =
        await API.tonneDefekt.get.getTonneDeffektStreetsAndPlaces();

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setIsLoading(false));
    }
  },
);

export const getAdminTonneVerschwunden = createAsyncThunk(
  "tonneVerschwunden/admin/getAdminTonneVerschwunden",
  async (
    {
      direction,
      page,
      pageSize,
      searchValue,
      sort,
      signal,
      endDate,
      startDate,
      status,
      // type,
    }: {
      sort?: string;
      direction?: string;
      searchValue?: string;
      page?: number;
      pageSize?: number;
      startDate?: Date | null | string;
      endDate?: Date | null | string;
      status?: string[];
      signal: AbortSignal;
      // type: string[];
    },
    { dispatch },
  ) => {
    try {
      dispatch(setAdminLoader(true));
      const data = API.tonneVerschwunden.get.getAdminTonneVerschwunden({
        direction,
        page,
        pageSize,
        searchValue,
        sort,
        signal,
        endDate,
        startDate,
        status,
      });

      return data;
    } catch (error: any) {
      if (error !== "Request canceled") {
        dispatch(setIsToasterError(true));
        dispatch(setErrorMessage(error?.response?.data?.message));
      }
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);

export const createTonneVerschwunden = createAsyncThunk(
  "tonneVerschwunden/client/createTonneVerschwunden",
  async (
    data: ITonneVerschwundenCreateBody & {
      acceptHandler: () => void;
      solution?: string;
    },
    { dispatch },
  ) => {
    const { acceptHandler, ...body } = data;
    try {
      dispatch(setIsLoading(true));
      const data =
        await API.tonneVerschwunden.post.createTonneVerschwunden(body);
      if (!data.error) {
        acceptHandler();
      }

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setIsLoading(false));
    }
  },
);

export const changeTonneVerschwundenItemsStatuses = createAsyncThunk(
  "tonneVerschwunden/admin/changeTonneVerschwundenItemsStatuses",
  async ({ ids, status }: { ids: string[]; status: string }, { dispatch }) => {
    try {
      dispatch(setAdminLoader(true));

      const data = await API.tonneVerschwunden.put.changeItemsStatuses({
        ids,
        status,
      });

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);

export const getAdminTonneVerschwundenItemById = createAsyncThunk(
  "tonneVerschwunden/admin/getAdminTonneVerschwundenItemById",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setAdminLoader(true));
      const data =
        await API.tonneVerschwunden.get.getAdminTonneVerschwundenItemById(id);

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);
export const updateStatusById = createAsyncThunk(
  "tonneVerschwunden/admin/updateStatusById",
  async (
    {
      id,
      status,
      message,
      calenderWeek,
      updatedStatusType,
    }: {
      id: string;
      status: string;
      message?: string;
      calenderWeek?: string;
      updatedStatusType?: string;
    },
    { dispatch },
  ) => {
    try {
      dispatch(setAdminLoader(true));
      const data = await API.tonneVerschwunden.put.updateStatusById({
        id,
        status,
        message,
        calenderWeek,
        updatedStatusType,
      });

      return data;
    } catch (error: any) {
      dispatch(setIsToasterError(true));
      dispatch(setErrorMessage(error?.response?.data?.message));
    } finally {
      dispatch(setAdminLoader(false));
    }
  },
);

const tonneVerschwundenSlice = createSlice({
  name: "tonneVerschwunden",
  initialState,
  reducers: {
    clearAdminTonneDefekt: (state) => {
      state.admin.itemsCount = 0;
      state.admin.tonneVerschwundenData = [];
    },
    clearAdminTonneDefektItem: (state) => {
      state.admin.tonneVerschwundenDataItem = null;
      state.admin.isTonneVerschwundenDataLoading = false;
    },
    clearErrorMessage: (state) => {
      state.client.errorMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getTonneDeffektStreetsAndPlaces.pending,
      (state) => {},
    );
    builder.addCase(
      getTonneDeffektStreetsAndPlaces.fulfilled,
      (state, action) => {
        if (action.payload) {
          state.client.places = action.payload.places;
          state.client.streets = action.payload.streets;
        }
      },
    );
    builder.addCase(
      getTonneDeffektStreetsAndPlaces.rejected,
      (state) => {},
    );
    builder.addCase(getAdminTonneVerschwunden.pending, (state) => {});
    builder.addCase(getAdminTonneVerschwunden.fulfilled, (state, action) => {
      if (action.payload) {
        state.admin.itemsCount = action.payload.totalCount;
        state.admin.tonneVerschwundenData = [...action.payload.data];
      }
    });
    builder.addCase(getAdminTonneVerschwunden.rejected, (state) => {});
    builder.addCase(
      changeTonneVerschwundenItemsStatuses.pending,
      (state) => {},
    );
    builder.addCase(
      changeTonneVerschwundenItemsStatuses.fulfilled,
      (state, action) => {
        if (action.payload) {
          state.admin.tonneVerschwundenData =
            state.admin.tonneVerschwundenData.map((el) => {
              const item = action.payload?.find((i) => i.id === el.id);
              if (item) {
                return item;
              }
              return el;
            });
        }
      },
    );
    builder.addCase(
      changeTonneVerschwundenItemsStatuses.rejected,
      (state) => {},
    );
    builder.addCase(getAdminTonneVerschwundenItemById.pending, (state) => {
      state.admin.isTonneVerschwundenDataLoading = true;
    });
    builder.addCase(
      getAdminTonneVerschwundenItemById.fulfilled,
      (state, action) => {
        if (action.payload) {
          state.admin.tonneVerschwundenDataItem = action.payload;
        }
        state.admin.isTonneVerschwundenDataLoading = false;
      },
    );
    builder.addCase(getAdminTonneVerschwundenItemById.rejected, (state) => {
      state.admin.isTonneVerschwundenDataLoading = false;
    });
    builder.addCase(updateStatusById.pending, (state) => {
      state.admin.isTonneVerschwundenDataLoading = true;
    });
    builder.addCase(updateStatusById.fulfilled, (state, action) => {
      if (action.payload) {
        state.admin.tonneVerschwundenDataItem = action.payload;
      }
      state.admin.isTonneVerschwundenDataLoading = false;
    });
    builder.addCase(updateStatusById.rejected, (state) => {
      state.admin.isTonneVerschwundenDataLoading = false;
    });
    builder.addCase(createTonneVerschwunden.pending, (state) => {
      state.client.errorMessage = "";
    });
    builder.addCase(createTonneVerschwunden.fulfilled, (state, action) => {
      if (action.payload?.error) {
        state.client.errorMessage = action.payload?.errorMessage ?? "";
      }
    });
    builder.addCase(createTonneVerschwunden.rejected, (state) => {});
  },
});

export const {
  clearAdminTonneDefekt,
  clearAdminTonneDefektItem,
  clearErrorMessage,
} = tonneVerschwundenSlice.actions;

export const tonneVerschwundenReducer = tonneVerschwundenSlice.reducer;
